import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Calendar, Clock, DollarSign, Music, Mic2, ArrowLeft, Volume2, Users, ChevronRight, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, errMsg } from "../lib/api";
import { PipeText } from "../lib/pipeText";
import { fmtDate, fmtTime } from "../components/Cards";
import { Countdown } from "../components/Marketplace";
import { Reveal, SectionTitle, useSeo } from "../components/VIP";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "../components/ui/dialog";

const inp = "w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-4 py-2.5 outline-none focus:border-rose-500 text-sm";
const bidTone = { accepted: "bg-emerald-500/20 text-emerald-300", shortlisted: "bg-amber-500/20 text-amber-300", rejected: "bg-zinc-500/20 text-zinc-300", withdrawn: "bg-zinc-500/20 text-zinc-300", submitted: "bg-white/10 text-zinc-300" };

function BidDialog({ gig, onBid }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [myArtists, setMyArtists] = useState([]);
  const [f, setF] = useState({ artist_id: "", bid_amount: "", proposed_set_length: "", message: "", equipment_notes: "", availability_confirmed: false });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  useEffect(() => { if (open && user) api.get("/my/artists").then((r) => { const pub = r.data.filter((a) => a.status === "published"); setMyArtists(pub); if (pub[0]) set("artist_id", pub[0].id); }); /* eslint-disable-next-line */ }, [open, user]);

  const submit = async () => {
    if (!f.artist_id) { toast.error("Select an artist profile"); return; }
    try { await api.post(`/gigs/${gig.id}/bids`, f); toast.success("Bid submitted!"); setOpen(false); onBid(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (!user) return <Link to="/login" className="block w-full text-center bg-rose-500 hover:bg-rose-600 font-bold px-6 py-3 rounded-full" data-testid="gig-login-to-bid">Log in to bid</Link>;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button className="w-full bg-rose-500 hover:bg-rose-600 font-bold px-6 py-3 rounded-full" data-testid="gig-bid-btn">Place a bid</button></DialogTrigger>
      <DialogContent className="bg-[#111] border-white/10 text-white max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-2xl">Bid on this gig</DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm">Submit your offer for "{gig.title}".</DialogDescription>
        </DialogHeader>
        {myArtists.length === 0 ? (
          <p className="text-sm text-zinc-400">You need a published artist profile to bid. <Link to="/submit" className="text-rose-400">Submit one →</Link></p>
        ) : (
          <div className="space-y-3">
            <select className={inp} value={f.artist_id} onChange={(e) => set("artist_id", e.target.value)} data-testid="bid-artist">{myArtists.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select>
            <input className={inp} placeholder="Bid amount (e.g. $1200)" value={f.bid_amount} onChange={(e) => set("bid_amount", e.target.value)} data-testid="bid-amount" />
            <input className={inp} placeholder="Proposed set length (e.g. 2 x 45min)" value={f.proposed_set_length} onChange={(e) => set("proposed_set_length", e.target.value)} data-testid="bid-setlength" />
            <textarea rows={3} className={inp} placeholder="Message to the poster" value={f.message} onChange={(e) => set("message", e.target.value)} data-testid="bid-message" />
            <input className={inp} placeholder="Equipment notes" value={f.equipment_notes} onChange={(e) => set("equipment_notes", e.target.value)} />
            <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={f.availability_confirmed} onChange={(e) => set("availability_confirmed", e.target.checked)} data-testid="bid-availability" /> I confirm I'm available on {fmtDate(gig.event_date)}</label>
            <button onClick={submit} className="w-full bg-rose-500 hover:bg-rose-600 font-bold py-3 rounded-lg" data-testid="bid-submit">Submit bid</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function GigDetail() {
  const { slug } = useParams();
  const [g, setG] = useState(null);
  const load = () => api.get(`/gigs/${slug}`).then((r) => setG(r.data)).catch(() => setG(false));
  useEffect(() => { load(); window.scrollTo(0, 0); /* eslint-disable-next-line */ }, [slug]);
  const setBidStatus = async (id, status) => { try { await api.patch(`/bids/${id}`, { status }); toast.success(`Bid ${status}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  useSeo(g ? `${g.title} | Gulf Coast VIP Gigs` : null, g?.description);

  if (g === false) return <div className="py-40 text-center text-zinc-500">Gig not found.</div>;
  if (!g) return <div className="py-40 text-center text-zinc-500">Loading...</div>;

  const detail = (Icon, label, val) => val ? <div className="flex items-start gap-3"><Icon size={18} className="text-rose-400 mt-0.5 shrink-0" /><div><p className="text-xs text-zinc-500">{label}</p><p className="text-sm">{val}</p></div></div> : null;

  return (
    <div data-testid="gig-detail" className="px-6 md:px-12 lg:px-20 py-10">
      <Link to="/gigs" className="flex items-center gap-1 text-sm text-zinc-400 hover:text-white mb-6"><ArrowLeft size={16} /> All gigs</Link>
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <span className="overline">{g.performance_type_needed}{g.genre_needed ? ` · ${g.genre_needed}` : ""}</span>
          <span className={`text-xs px-3 py-1 rounded-full capitalize ${g.status === "open" ? "bg-emerald-500/20 text-emerald-300" : "bg-zinc-500/20 text-zinc-300"}`}>{g.status}</span>
        </div>
        <h1 className="font-display font-black text-4xl md:text-6xl max-w-4xl">{g.title}</h1>
        <p className="text-zinc-300 mt-4 flex items-center gap-2"><MapPin size={16} className="text-rose-400" /> {g.location_name || g.venue_name}, {g.city}, {g.state}</p>
      </motion.div>

      <div className="grid lg:grid-cols-3 gap-10 mt-10">
        <div className="lg:col-span-2 space-y-12">
          <Reveal>
            <SectionTitle kicker="The opportunity" title="About this gig" />
            <PipeText text={g.description} className="text-zinc-300 leading-relaxed mt-6 text-lg" />
          </Reveal>

          <Reveal>
            <SectionTitle kicker="Bids" title={`Artist bids (${g.bids?.length || 0})`} />
            {!g.is_owner && <p className="text-xs text-zinc-500 mt-2 flex items-center gap-1"><Lock size={11} /> Bid amounts are private — only the poster can see them.</p>}
            <div className="space-y-3 mt-6">
              {(g.bids || []).map((b) => (
                <div key={b.id} className="flex items-center gap-4 bg-[#111] border border-white/10 rounded-2xl p-3" data-testid={`bid-${b.id}`}>
                  {b.artist ? <img src={fileUrl(b.artist.profile_image || b.artist.hero_image)} alt="" className="w-14 h-14 rounded-xl object-cover" /> : <div className="w-14 h-14 rounded-xl bg-white/5" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-display font-bold">{b.artist?.name || "Artist"}</p>
                      {b.artist?.logo && <img src={fileUrl(b.artist.logo)} alt="" className="w-5 h-5 rounded object-cover" />}
                      <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${bidTone[b.status]}`}>{b.status}</span>
                    </div>
                    <p className="text-sm text-zinc-400">{b.artist?.genre} {b.message ? `· "${b.message}"` : ""}</p>
                  </div>
                  <div className="text-right shrink-0">
                    {b.bid_amount ? (
                      <p className="font-display font-bold text-rose-400" data-testid={`bid-amount-${b.id}`}>{String(b.bid_amount).startsWith("$") ? b.bid_amount : `$${b.bid_amount}`}</p>
                    ) : b.amount_hidden ? (
                      <p className="text-xs text-zinc-500 flex items-center justify-end gap-1" data-testid={`bid-amount-hidden-${b.id}`}><Lock size={11} /> Amount hidden</p>
                    ) : null}
                    {b.artist && <Link to={`/artists/${b.artist.slug}`} className="text-xs text-zinc-400 hover:text-white flex items-center justify-end gap-1">View <ChevronRight size={12} /></Link>}
                    {g.is_owner && b.status !== "accepted" && b.status !== "withdrawn" && (
                      <div className="flex items-center gap-1.5 justify-end mt-2">
                        {b.status !== "shortlisted" && <button onClick={() => setBidStatus(b.id, "shortlisted")} className="text-[11px] px-2 py-1 rounded bg-amber-500/15 text-amber-300 hover:bg-amber-500/25" data-testid={`bid-shortlist-${b.id}`}>Shortlist</button>}
                        <button onClick={() => setBidStatus(b.id, "accepted")} className="text-[11px] px-2 py-1 rounded bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25" data-testid={`bid-accept-${b.id}`}>Accept</button>
                        {b.status !== "rejected" && <button onClick={() => setBidStatus(b.id, "rejected")} className="text-[11px] px-2 py-1 rounded bg-rose-500/15 text-rose-300 hover:bg-rose-500/25" data-testid={`bid-reject-${b.id}`}>Reject</button>}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {(!g.bids || g.bids.length === 0) && <p className="text-zinc-500">No bids yet — be the first to apply.</p>}
            </div>
          </Reveal>
        </div>

        <aside>
          <div className="lg:sticky lg:top-20 bg-gradient-to-b from-[#161616] to-[#0d0d0d] border border-white/10 rounded-3xl p-7 space-y-5 glow-ring">
            {g.application_deadline && (
              <div>
                <p className="text-xs text-zinc-500 mb-2">Applications close in</p>
                <Countdown to={`${g.application_deadline}T23:59:59`} endLabel="Applications closed" />
              </div>
            )}
            {g.status === "open" ? <BidDialog gig={g} onBid={load} /> : <p className="text-sm text-zinc-400">This gig is {g.status} and no longer accepting bids.</p>}
            <div className="pt-4 border-t border-white/10 space-y-4">
              {detail(Calendar, "Date", fmtDate(g.event_date))}
              {detail(Clock, "Time", `${fmtTime(g.start_time)}${g.end_time ? ` – ${fmtTime(g.end_time)}` : ""}`)}
              {detail(DollarSign, "Budget", (g.budget_min || g.budget_max) ? `$${g.budget_min}${g.budget_max ? ` – $${g.budget_max}` : ""} (${g.payment_type})` : g.payment_type)}
              {detail(Mic2, "Performance", g.performance_type_needed)}
              {detail(Music, "Genre", g.genre_needed)}
              {detail(Volume2, "Sound system", g.sound_system_provided)}
              {detail(Users, "Age policy", g.age_restriction)}
              {detail(MapPin, "Posted by", g.posted_by_name)}
              {g.booking_requirements && <div><p className="text-xs text-zinc-500">Requirements</p><p className="text-sm">{g.booking_requirements}</p></div>}
              {g.equipment_provided && <div><p className="text-xs text-zinc-500">Equipment provided</p><p className="text-sm">{g.equipment_provided}</p></div>}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
